
/* === cart-upsell.js === */
if (!customElements.get('cart-upsell')) {
  customElements.define('cart-upsell', class CartUpsell extends HTMLElement {
    constructor() {
      super();
      this.productsContainer = null;
      this.boundCartUpdated = this.handleCartUpdated.bind(this);
    }

    connectedCallback() {
      this.productsContainer = this.querySelector('[data-upsell-products]');
      this.source = this.dataset.source;
      this.limit = parseInt(this.dataset.limit) || 4;
      this.buttonStyle = this.dataset.buttonStyle || 'secondary';

      this.strings = window.cartUpsellStrings || {
        add: 'Add',
        soldOut: 'Sold out'
      };

      if (this.hasAttribute('data-load-recommendations')) {
        this.loadRecommendations();
      }

      this.addEventListener('click', this.handleClick.bind(this));
      window.addEventListener('cart:updated', this.boundCartUpdated);
    }

    disconnectedCallback() {
      window.removeEventListener('cart:updated', this.boundCartUpdated);
    }

    handleCartUpdated() {
      if (this.source === 'recommendations') {
        this.loadRecommendations();
      }
    }

    handleClick(e) {
      const addBtn = e.target.closest('[data-upsell-add]');
      if (addBtn) {
        e.preventDefault();
        this.addToCart(addBtn);
        return;
      }

      const toggleBtn = e.target.closest('[data-upsell-toggle]');
      if (toggleBtn) {
        this.toggleMobileContent(toggleBtn);
      }
    }

    toggleMobileContent(button) {
      const content = this.querySelector('[data-upsell-content]');
      const isExpanded = button.getAttribute('aria-expanded') === 'true';

      button.setAttribute('aria-expanded', !isExpanded);
      this.classList.toggle('cart-upsell--expanded', !isExpanded);

      if (content) {
        if (isExpanded) {
          content.style.maxHeight = '0';
        } else {
          content.style.maxHeight = content.scrollHeight + 'px';
          content.addEventListener('transitionend', () => {
            if (this.classList.contains('cart-upsell--expanded')) {
              content.style.maxHeight = 'none';
            }
          }, { once: true });
        }
      }
    }

    async loadRecommendations() {
      if (!window.CartAPI) {
        setTimeout(() => this.loadRecommendations(), 100);
        return;
      }

      const cartData = await window.CartAPI.get();
      if (!cartData.items || cartData.items.length === 0) {
        this.style.display = 'none';
        return;
      }

      const eligibleItems = cartData.items
        .filter(item => item.properties?._gift_tier !== 'true')
        .sort((a, b) => b.final_line_price - a.final_line_price);

      if (eligibleItems.length === 0) {
        this.style.display = 'none';
        return;
      }

      try {
        const allRecommendations = await this.fetchMergedRecommendations(eligibleItems);
        const cartProductIds = cartData.items.map(item => item.product_id);
        const filteredProducts = allRecommendations.filter(p => !cartProductIds.includes(p.id));

        if (filteredProducts.length > 0) {
          this.renderProducts(filteredProducts);
          this.style.display = '';
        } else {
          this.style.display = 'none';
        }
      } catch {
        this.style.display = 'none';
      }
    }

    async fetchMergedRecommendations(cartItems) {
      const itemsToFetch = cartItems.slice(0, 3);
      const fetchLimit = this.limit * 2;

      const requests = itemsToFetch.map(async item => {
        const complementaryUrl = `${window.Shopify.routes.root}recommendations/products.json?product_id=${item.product_id}&limit=${fetchLimit}&intent=complementary`;
        try {
          const response = await fetch(complementaryUrl);
          const data = await response.json();
          if (data.products && data.products.length > 0) {
            return data;
          }
        } catch {  }

        const relatedUrl = `${window.Shopify.routes.root}recommendations/products.json?product_id=${item.product_id}&limit=${fetchLimit}&intent=related`;
        try {
          const response = await fetch(relatedUrl);
          return await response.json();
        } catch {
          return { products: [] };
        }
      });

      const results = await Promise.all(requests);
      const productScores = new Map();

      for (const result of results) {
        if (result.products) {
          for (const product of result.products) {
            const existing = productScores.get(product.id);
            if (existing) {
              existing.score += 1;
            } else {
              productScores.set(product.id, { product, score: 1 });
            }
          }
        }
      }

      return Array.from(productScores.values())
        .sort((a, b) => {
          if (b.score !== a.score) return b.score - a.score;
          return b.product.price - a.product.price;
        })
        .map(item => item.product)
        .slice(0, this.limit);
    }

    renderProducts(products) {
      const html = products.slice(0, this.limit).map(product => `
        <div class="cart-upsell__item" data-upsell-item data-product-id="${product.id}">
          <div class="cart-upsell__image">
            ${this.getProductImageHtml(product)}
          </div>
          <div class="cart-upsell__info">
            <p class="cart-upsell__product-title">${window.escapeHtml(product.title)}</p>
            <p class="cart-upsell__price">${window.Currency.formatMoney(product.price)}</p>
          </div>
          <button class="cart-upsell__add button button--${this.buttonStyle}"
                  data-upsell-add
                  data-variant-id="${product.variants[0].id}"
                  ${!product.available ? 'disabled' : ''}>
            ${product.available ? this.strings.add : this.strings.soldOut}
          </button>
        </div>
      `).join('');

      this.productsContainer.innerHTML = html;
    }

    getProductImageHtml(product) {
      const imageUrl = this.getImageUrl(product.featured_image, 150);
      if (!imageUrl) return '';

      return `<img src="${imageUrl}"
                   alt="${window.escapeHtml(product.title)}"
                   loading="lazy" width="60" height="60">`;
    }

    getImageUrl(image, width) {
      if (!image) return '';
      let url = typeof image === 'object' ? image.src : image;
      if (!url) return '';

      if (url.includes('cdn.shopify.com')) {
        return url.replace(/(_\d+x\d*)?(\.[^.?]+)(\?.*)?$/, `_${width}x$2$3`);
      }
      return url;
    }

    async addToCart(button) {
      const variantId = button.dataset.variantId;
      button.disabled = true;
      button.classList.add('loading');
      const originalText = button.textContent;

      try {
        await window.CartAPI.add({ id: variantId, quantity: 1 });
        window.dispatchEvent(new CustomEvent('cart:updated'));

        button.textContent = '✓';
        setTimeout(() => {
          button.textContent = originalText;
          button.disabled = false;
          button.classList.remove('loading');
        }, 1000);
      } catch {
        button.disabled = false;
        button.classList.remove('loading');
      }
    }
  });
}

